import React, { useCallback, useContext, useMemo, useState } from "react"

type AvatarEditorModalActions = {
  openModal: () => void
  closeModal: () => void
}

// Two contexts: the actions never change identity, so consumers that only
// open the modal (the game page) do not re-render when it opens or closes.
const AvatarEditorModalActionsContext =
  React.createContext<AvatarEditorModalActions>({
    openModal: () => {},
    closeModal: () => {},
  })
const AvatarEditorModalStateContext = React.createContext<boolean>(false)

export const AvatarEditorModalProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)

  const openModal = useCallback(() => setIsOpen(true), [])
  const closeModal = useCallback(() => setIsOpen(false), [])
  const actions = useMemo(
    () => ({ openModal, closeModal }),
    [openModal, closeModal],
  )

  return (
    <AvatarEditorModalActionsContext.Provider value={actions}>
      <AvatarEditorModalStateContext.Provider value={isOpen}>
        {children}
      </AvatarEditorModalStateContext.Provider>
    </AvatarEditorModalActionsContext.Provider>
  )
}

export const useAvatarEditorModalActions = () =>
  useContext(AvatarEditorModalActionsContext)

export const useAvatarEditorModal = () => {
  const actions = useContext(AvatarEditorModalActionsContext)
  const isOpen = useContext(AvatarEditorModalStateContext)
  return { ...actions, isOpen }
}
